import { useRef, useState, type ChangeEvent, type FocusEvent, type KeyboardEvent } from 'react';
import { Button, FormHelperText, MenuItem, Select, TextField } from '@mui/material';
import classNames from 'classnames';
import { NumericFormat } from 'react-number-format';

import { MyButton } from '@/components';
import type { AdFieldBedsEnum } from '@/types';

import styles from './AdsFilters.module.scss';
import { ReactComponent as CaretDown } from './caretDown.svg';

interface BedsOption {
  label: string;
  value: AdFieldBedsEnum;
}

interface FilterActionsRowProps {
  areaLabel: string;
  areaMax: number | string | undefined;
  areaMin: number | string | undefined;
  areaUnit?: string;
  bedsLabel: string;
  bedsOptions: BedsOption[];
  isAreaRangeInvalid: boolean;
  isPriceRangeInvalid: boolean;
  onAreaBlur: () => void;
  onBedsChange: (value: AdFieldBedsEnum) => void;
  onMaximumAreaChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onMaximumPriceChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onMinimumAreaChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onMinimumPriceChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onPriceBlur: () => void;
  priceLabel: string;
  priceMax: number | string | undefined;
  priceMin: number | string | undefined;
  selectedBeds: AdFieldBedsEnum[];
}

interface FilterPrimaryActionsProps {
  activeFiltersCount: number;
  onMoreFiltersOpen: () => void;
  onReset: () => void;
}

export const FilterPrimaryActions = ({
  activeFiltersCount,
  onMoreFiltersOpen,
  onReset,
}: FilterPrimaryActionsProps) => (
  <div className={styles.actionsControl} data-testid='filter-actions-control'>
    <div className={styles.filterActionsGroup}>
      <div className={styles.filterActionItem}>
        <MyButton
          data={{
            buttonName: 'More filters',
            buttonsIcon: activeFiltersCount > 0,
            icon:
              activeFiltersCount > 0 ? (
                <span
                  aria-hidden='true'
                  className={styles.filterCountBadge}
                  data-testid='active-filters-count'
                >
                  {activeFiltersCount}
                </span>
              ) : undefined,
            iconPosition: 'end',
            variant: 'contained',
          }}
          onClick={onMoreFiltersOpen}
        />
      </div>
      <div className={styles.filterActionItem}>
        <MyButton
          data={{
            buttonName: 'Reset',
            styleType: 'reset',
            variant: 'contained',
          }}
          disabled={activeFiltersCount === 0}
          onClick={onReset}
        />
      </div>
    </div>
  </div>
);

export const FilterActionsRow = ({
  areaLabel,
  areaMax,
  areaMin,
  areaUnit,
  bedsLabel,
  bedsOptions,
  isAreaRangeInvalid,
  isPriceRangeInvalid,
  onAreaBlur,
  onBedsChange,
  onMaximumAreaChange,
  onMaximumPriceChange,
  onMinimumAreaChange,
  onMinimumPriceChange,
  onPriceBlur,
  priceLabel,
  priceMax,
  priceMin,
  selectedBeds,
}: FilterActionsRowProps) => {
  const [isAreaDropdownOpen, setIsAreaDropdownOpen] = useState(false);
  const [isPriceDropdownOpen, setIsPriceDropdownOpen] = useState(false);
  const areaContentRef = useRef<HTMLDivElement>(null);
  const areaCommitHandledRef = useRef(false);
  const areaCloseWithoutApplyRef = useRef(false);
  const priceContentRef = useRef<HTMLDivElement>(null);
  const priceCommitHandledRef = useRef(false);
  const closeWithoutApplyRef = useRef(false);

  const handleAreaDropdownOpen = () => {
    areaCommitHandledRef.current = false;
    areaCloseWithoutApplyRef.current = false;
    setIsAreaDropdownOpen(true);
  };

  const applyAreaAndClose = () => {
    if (!areaCommitHandledRef.current) {
      areaCommitHandledRef.current = true;
      onAreaBlur();
    }
    setIsAreaDropdownOpen(false);
  };

  const closeAreaWithoutApply = () => {
    areaCloseWithoutApplyRef.current = true;
    setIsAreaDropdownOpen(false);
  };

  const handleAreaDropdownClose = () => {
    if (areaCloseWithoutApplyRef.current) {
      setIsAreaDropdownOpen(false);
      return;
    }
    applyAreaAndClose();
  };

  const handleAreaInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    const nextFocusedElement = event.relatedTarget;

    if (
      nextFocusedElement instanceof Node &&
      areaContentRef.current?.contains(nextFocusedElement)
    ) {
      return;
    }

    if (!areaCloseWithoutApplyRef.current) {
      applyAreaAndClose();
    }
  };

  const handleAreaKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      applyAreaAndClose();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeAreaWithoutApply();
    }
  };

  const handlePriceDropdownOpen = () => {
    priceCommitHandledRef.current = false;
    closeWithoutApplyRef.current = false;
    setIsPriceDropdownOpen(true);
  };

  const applyPriceAndClose = () => {
    if (!priceCommitHandledRef.current) {
      priceCommitHandledRef.current = true;
      onPriceBlur();
    }
    setIsPriceDropdownOpen(false);
  };

  const closePriceWithoutApply = () => {
    closeWithoutApplyRef.current = true;
    setIsPriceDropdownOpen(false);
  };

  const handlePriceDropdownClose = () => {
    if (closeWithoutApplyRef.current) {
      setIsPriceDropdownOpen(false);
      return;
    }
    applyPriceAndClose();
  };

  const handlePriceInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    const nextFocusedElement = event.relatedTarget;

    if (
      nextFocusedElement instanceof Node &&
      priceContentRef.current?.contains(nextFocusedElement)
    ) {
      return;
    }

    if (!closeWithoutApplyRef.current) {
      applyPriceAndClose();
    }
  };

  const handlePriceKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      applyPriceAndClose();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closePriceWithoutApply();
    }
  };

  return (
    <>
      <div className={styles.bedsControl}>
        <Select
          value={'-1'}
          className={styles.filterSelect}
          IconComponent={({ className }) => (
            <CaretDown
              className={classNames(styles.filterSelectCaret, {
                [styles.filterCaretUp]: className.toString().includes('iconOpen'),
                [styles.filterCaretDown]: !className.toString().includes('iconOpen'),
              })}
            />
          )}
        >
          <MenuItem style={{ display: 'none' }} hidden key={1} value={'-1'}>
            {bedsLabel}
          </MenuItem>
          <div className={classNames(styles.selectInnerContainer, styles.filterBeds)}>
            <FormHelperText>
              <span className={styles.selectInnerTitle}>Beds</span>
            </FormHelperText>
            <div className={styles.selectInnerContent}>
              {bedsOptions.map(({ label, value }) => (
                <Button
                  key={value}
                  className={classNames(styles.itemButton, {
                    [styles.active]: selectedBeds.includes(value),
                  })}
                  onClick={() => onBedsChange(value)}
                  variant='contained'
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </Select>
      </div>
      <div className={styles.priceControl}>
        <Select
          value={'-1'}
          open={isPriceDropdownOpen}
          onOpen={handlePriceDropdownOpen}
          onClose={handlePriceDropdownClose}
          className={styles.filterSelect}
          IconComponent={({ className }) => (
            <CaretDown
              className={classNames(styles.filterSelectCaret, {
                [styles.filterCaretUp]: className.toString().includes('iconOpen'),
                [styles.filterCaretDown]: !className.toString().includes('iconOpen'),
              })}
            />
          )}
        >
          <MenuItem style={{ display: 'none' }} hidden key={1} value={'-1'}>
            {priceLabel}
          </MenuItem>
          <div
            className={classNames(styles.selectInnerContainer, styles.filterPrice)}
            ref={priceContentRef}
          >
            <div className={classNames(styles.selectInnerContent, styles.filterPrice)}>
              <div>
                <FormHelperText>
                  <span className={styles.selectInnerLabel}>Minimum</span>
                </FormHelperText>
                <NumericFormat
                  customInput={TextField}
                  value={priceMin}
                  allowNegative={false}
                  thousandSeparator=' '
                  decimalScale={0}
                  onKeyDown={handlePriceKeyDown}
                  onBlur={handlePriceInputBlur}
                  onChange={onMinimumPriceChange}
                />
              </div>
              <div>
                <FormHelperText>
                  <span className={styles.selectInnerLabel}>Maximum</span>
                </FormHelperText>
                <NumericFormat
                  customInput={TextField}
                  value={priceMax}
                  allowNegative={false}
                  thousandSeparator=' '
                  decimalScale={0}
                  onKeyDown={handlePriceKeyDown}
                  onBlur={handlePriceInputBlur}
                  onChange={onMaximumPriceChange}
                  error={isPriceRangeInvalid}
                  helperText={
                    isPriceRangeInvalid ? 'max value should be more or equal than min value' : ''
                  }
                />
              </div>
            </div>
          </div>
        </Select>
      </div>
      <div className={styles.areaControl} data-testid='quick-filter-area'>
        <Select
          value={'-1'}
          open={isAreaDropdownOpen}
          onOpen={handleAreaDropdownOpen}
          onClose={handleAreaDropdownClose}
          className={styles.filterSelect}
          IconComponent={({ className }) => (
            <CaretDown
              className={classNames(styles.filterSelectCaret, {
                [styles.filterCaretUp]: className.toString().includes('iconOpen'),
                [styles.filterCaretDown]: !className.toString().includes('iconOpen'),
              })}
            />
          )}
        >
          <MenuItem style={{ display: 'none' }} hidden key={1} value={'-1'}>
            {areaLabel}
          </MenuItem>
          <div
            className={classNames(styles.selectInnerContainer, styles.filterArea)}
            ref={areaContentRef}
          >
            <div className={classNames(styles.selectInnerContent, styles.filterArea)}>
              <div>
                <FormHelperText>
                  <span className={styles.selectInnerLabel}>
                    Minimum area{areaUnit ? ` (${areaUnit})` : ''}
                  </span>
                </FormHelperText>
                <NumericFormat
                  customInput={TextField}
                  value={areaMin}
                  allowNegative={false}
                  thousandSeparator=' '
                  decimalScale={0}
                  inputProps={{
                    'aria-label': `Minimum area${areaUnit ? ` (${areaUnit})` : ''}`,
                  }}
                  onKeyDown={handleAreaKeyDown}
                  onBlur={handleAreaInputBlur}
                  onChange={onMinimumAreaChange}
                />
              </div>
              <div>
                <FormHelperText>
                  <span className={styles.selectInnerLabel}>
                    Maximum area{areaUnit ? ` (${areaUnit})` : ''}
                  </span>
                </FormHelperText>
                <NumericFormat
                  customInput={TextField}
                  value={areaMax}
                  allowNegative={false}
                  thousandSeparator=' '
                  decimalScale={0}
                  inputProps={{
                    'aria-label': `Maximum area${areaUnit ? ` (${areaUnit})` : ''}`,
                  }}
                  onKeyDown={handleAreaKeyDown}
                  onBlur={handleAreaInputBlur}
                  onChange={onMaximumAreaChange}
                  error={isAreaRangeInvalid}
                  helperText={
                    isAreaRangeInvalid ? 'max value should be more or equal than min value' : ''
                  }
                />
              </div>
            </div>
          </div>
        </Select>
      </div>
    </>
  );
};
